import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { REGEX_EMAIL_VALIDATION } from '../common/constants'
import { useAuthContext } from '../contexts'
import { Input, Button } from '../shared/components/ui'

export function ForgotPasswordPage() {
  const { resetPassword } = useAuthContext()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    setEnabled(REGEX_EMAIL_VALIDATION.test(email))
  }, [email])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!enabled) {
      setError('Please enter a valid email address')
      return
    }

    try {
      setIsLoading(true)
      setError('')
      await resetPassword(email)
      setSuccess(true)
    } catch (error: any) {
      setError(error.message || 'Failed to send reset email')
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-center text-2xl font-bold text-gray-900 mb-4">Check your email</h2>
            <div className="bg-green-50 border border-green-200 rounded-md p-4 text-sm text-green-700 mb-4">
              We've sent a password reset link to <strong>{email}</strong>
            </div>
            <p className="text-center text-sm text-gray-600 mb-4">
              Didn't receive the email? Check your spam folder or{' '}
              <button
                onClick={() => setSuccess(false)}
                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
              >
                try again
              </button>
            </p>
            <div className="text-center">
              <Link className="text-sm text-blue-600 hover:text-blue-800 hover:underline" to="/login">
                Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 rounded px-4 py-3 mb-6 text-sm text-center">
            {error}
          </div>
        )}
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-6">
            <h2 className="text-center text-2xl font-bold text-gray-900 mb-2">Forgot your password?</h2>
            <p className="text-center text-sm text-gray-600">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Email address</label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!REGEX_EMAIL_VALIDATION.test(email) && email !== ''}
                className="w-full"
              />
            </div>
            <Button
              type="submit"
              disabled={!enabled || isLoading}
              loading={isLoading}
              variant="primary"
              className="w-full"
            >
              Send reset link
            </Button>
            <div className="text-center mt-4">
              <Link className="text-sm text-blue-600 hover:text-blue-800 hover:underline" to="/login">
                Back to login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
